import { motion } from 'motion/react'
import type { LucideIcon } from 'lucide-react'
import { Suspense, useSyncExternalStore, type ReactNode } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

const GRAND_ECRAN = '(min-width: 64rem)' // le point de rupture `lg` de Tailwind

/** Vrai sur un écran d'ordinateur : le montage des volets en dépend, pour que le téléphone ne monte qu'une page à la fois. */
export function useGrandEcran() {
  return useSyncExternalStore(
    (notifier) => {
      const m = window.matchMedia(GRAND_ECRAN)
      m.addEventListener('change', notifier)
      return () => m.removeEventListener('change', notifier)
    },
    () => window.matchMedia(GRAND_ECRAN).matches,
    () => false,
  )
}

/**
 * Liste à gauche et détail à droite sur ordinateur. Sur téléphone, un seul des deux est affiché :
 * la liste, ou le détail dès qu'une ligne est ouverte (comme avant, une page à la fois).
 * `racine` est le chemin de la liste ; les routes filles forment le détail.
 */
export function MaitreDetail({ racine, liste }: { racine: string; liste: ReactNode }) {
  const { pathname } = useLocation()
  const ouvert = pathname.replace(/\/$/, '') !== racine
  const grand = useGrandEcran()
  return (
    <div className="flex flex-1 flex-col lg:grid lg:min-h-0 lg:grid-cols-[minmax(20rem,26rem)_1fr] lg:grid-rows-[minmax(0,1fr)]">
      {(grand || !ouvert) && <div className="flex flex-1 flex-col lg:min-h-0 lg:overflow-y-auto lg:border-r lg:border-slate-200">{liste}</div>}
      {(grand || ouvert) && (
        <motion.div
          key={pathname}
          className="flex flex-1 flex-col lg:min-h-0 lg:overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
        >
          <Suspense fallback={<main className="flex-1" />}>
            <Outlet />
          </Suspense>
        </motion.div>
      )}
    </div>
  )
}

/** Détail affiché tant qu'aucune ligne n'est ouverte (ordinateur seulement : sur téléphone, ce volet est masqué). */
export function DetailVide({ Icone, texte }: { Icone: LucideIcon; texte: string }) {
  return (
    <main className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center text-slate-600">
      <span className="flex size-16 items-center justify-center rounded-full bg-blue-100 text-nuit">
        <Icone aria-hidden className="size-8" />
      </span>
      <p className="max-w-xs">{texte}</p>
    </main>
  )
}

/** Colonne de lecture des pages sans liste : pleine largeur sur téléphone, centrée et bornée sur ordinateur. */
export function Colonne() {
  return (
    <div className="flex flex-1 flex-col lg:mx-auto lg:w-full lg:max-w-4xl">
      <Outlet />
    </div>
  )
}
